import { useState } from 'react';
import { doc, setDoc, addDoc, collection, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Pizza } from '../../types';
import Modal from '../Modal';

interface PizzaFormProps {
  pizza: Pizza | null;
  onClose: () => void;
}

export default function PizzaForm({ pizza, onClose }: PizzaFormProps) {
  const [formData, setFormData] = useState({
    name: pizza?.name || '',
    description: pizza?.description || '',
    imageUrl: pizza?.imageUrl || '',
    ingredients: pizza?.ingredients.join(', ') || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const pizzaData = {
        name: formData.name,
        description: formData.description,
        imageUrl: formData.imageUrl,
        ingredients: formData.ingredients.split(',').map(i => i.trim()),
        pizzerias: pizza?.pizzerias || [],
        updatedAt: new Date().toISOString()
      };

      if (pizza) {
        // Update existing pizza
        await setDoc(doc(db, 'pizze', pizza.id), pizzaData, { merge: true });
      } else {
        // Create new pizza
        await addDoc(collection(db, 'pizze'), {
          ...pizzaData,
          createdAt: new Date().toISOString()
        });
      }

      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!pizza || !window.confirm('Sei sicuro di voler eliminare questa pizza?')) {
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'pizze', pizza.id));
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <h2 className="text-2xl font-bold mb-4">
          {pizza ? 'Modifica Pizza' : 'Nuova Pizza'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">URL Immagine</label>
            <input
              type="url"
              value={formData.imageUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Ingredienti (separati da virgola)
            </label>
            <textarea
              value={formData.ingredients}
              onChange={(e) => setFormData(prev => ({ ...prev, ingredients: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
              rows={2}
              required
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-between pt-4">
            {pizza && (
              <button
                type="button"
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                disabled={loading}
              >
                Elimina
              </button>
            )}
            <div className="space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
                disabled={loading}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="bg-pizzaveg hover:bg-pizzaveg-dark text-white px-4 py-2 rounded-lg"
                disabled={loading}
              >
                {loading ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}